/**
 * Re-render when the API's rates replace the bundled fallback.
 *
 * The config lives in module state rather than React state, because prices are
 * computed from reducers and plain functions as well as components (see
 * `lib/pricing-config.ts`). The cost of that choice is that React does not know
 * when the value changes — so any component displaying money subscribes through
 * this hook. Without it, a component that rendered before the fetch resolved would
 * keep showing the fallback price until something else happened to re-render it.
 */

import { useEffect, useState } from "react";
import {
  currentPricingConfig,
  loadPricingConfig,
  onPricingConfigChange,
  pricingConfigIsLive,
} from "../lib/pricing-config";

export interface PricingConfigState {
  /** True once the API's rates are in use rather than the bundled fallback. */
  isLive: boolean;
  /** The rate card version in force, for support and debugging. */
  rateCardVersion: string;
}

export function usePricingConfig(): PricingConfigState {
  const [, forceRender] = useState(0);

  useEffect(() => {
    const unsubscribe = onPricingConfigChange(() => forceRender((n) => n + 1));
    /**
     * Also kick the fetch, not only subscribe. `index.tsx` starts it on load, but
     * a component can mount after a failed first attempt, and the call is
     * deduplicated — so asking again is free when it succeeded and a retry when it
     * did not.
     */
    loadPricingConfig().catch(() => {
      // Already reported where it was started; the fallback stays in place.
    });
    return unsubscribe;
  }, []);

  return {
    isLive: pricingConfigIsLive(),
    rateCardVersion: currentPricingConfig().rateCard.version,
  };
}
